/**
 * LA MARQUE — sous quel nom, quel logo et quelles couleurs ce cockpit se
 * présente, selon le DOMAINE qui le sert (RishFans, 13-10-2026).
 *
 * `app.rishfans.com` est ce même cockpit servi sous un autre domaine : les
 * partenaires RishFans y voient la marque RishFans (titre d'onglet, logo, or
 * sur noir, Poppins), pas « Dozka Cockpit ». UN point décide — ce module — et
 * les écrans (connexion, rail, barre de titre, thème) le LISENT ; aucun `if
 * (hostname…)` ne vit ailleurs. Une marque de plus = une entrée ici.
 *
 * ⚠️ Ce n'est PAS un contrôle d'accès : le domaine ne donne aucun droit. Ce
 * que voit un partenaire est tenu par son rôle (`partenaire_rishfans`, kiosque
 * d'une page) et par la donnée côté Cyrano.
 *
 * Ce module ne change que ce qu'un document lancé peut changer (titre, icônes,
 * couleur de barre, police). Le `<head>` servi — aperçus de lien, manifeste
 * PWA — est celui de la coquille du domaine, écrite au build (`coquilles`,
 * vite.config.ts).
 */

export type MarqueId = "dozka" | "rishfans" | "cyrano" | "helios";

export interface Marque {
  id: MarqueId;
  /** Titre d'onglet et nom affiché (écran de connexion, barre de titre mobile). */
  nom: string;
  /** Le wordmark de l'écran de connexion ; `null` = l'icône ronde historique. */
  logo: string | null;
  /** Petite icône : rail, barre de titre, favicon. */
  icone: string;
  /** Icône de l'écran de connexion quand il n'y a pas de wordmark. */
  iconeConnexion: string;
  /** Couleur d'accent par DÉFAUT (`--color-mark`) ; `null` = celle du thème
   *  (le bleu). Un utilisateur qui en choisit une autre garde la sienne. */
  accent: string | null;
  /** Thème par défaut ; "glass" est le verre dépoli de Dozka, "dark" un vrai
   *  fond noir. Un utilisateur qui en choisit un autre garde le sien. */
  themeParDefaut: "light" | "dark" | "glass";
  /** Couleur de la barre du navigateur (`theme-color`). */
  couleurDeBarre: string;
  /** Police de la marque, chargée à la demande ; `null` = celle du cockpit. */
  police: { famille: string; feuille: string } | null;
  /** Le nom qui remplace « Dozka » dans TOUS les textes traduits (cf
   *  `rebranderTraductions`) ; `null` = les textes restent tels quels. */
  nomDansLesTextes: string | null;
}

export const DOZKA: Marque = {
  id: "dozka",
  nom: "Dozka Cockpit",
  logo: null,
  icone: "/pwa-64x64.png",
  iconeConnexion: "/favicon.svg",
  accent: null,
  themeParDefaut: "glass",
  couleurDeBarre: "#050505",
  police: null,
  nomDansLesTextes: null,
};

export const RISHFANS: Marque = {
  id: "rishfans",
  nom: "RishFans",
  logo: "/rishfans/logo.png",
  icone: "/rishfans/icon.png",
  iconeConnexion: "/rishfans/icon.png",
  // L'or de la landing (rishfans.com), sur noir.
  accent: "#e9c46a",
  themeParDefaut: "dark",
  couleurDeBarre: "#000000",
  police: {
    famille: "Poppins",
    feuille:
      "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap",
  },
  nomDansLesTextes: null,
};

/**
 * CYRANO (Kayla, 13-10-2026) : l'agence de chatting. Ses chatteurs travaillent
 * sur `cyrano.work` — « la même interface, sauf que le branding va changer » :
 * même thème et même police que Dozka, mais plus un seul « Dozka » à l'écran,
 * textes traduits compris. La landing (`cyrano.team`) n'est pas le cockpit.
 */
export const CYRANO: Marque = {
  id: "cyrano",
  nom: "Cyrano",
  logo: "/rejoindre/cyrano-logo.png",
  icone: "/rejoindre/cyrano-icone.png",
  iconeConnexion: "/rejoindre/cyrano-icone.png",
  accent: null,
  themeParDefaut: "glass",
  couleurDeBarre: "#05070f",
  police: null,
  nomDansLesTextes: "Cyrano",
};

/**
 * HELIOS (27-10-2026) : l'écran de la campagne X de Kayla (apps/helios), sur
 * son serveur. La même mécanique que les autres marques — la librairie est
 * commune — avec ce qui le distingue : le jour par défaut, aucun accent
 * propre (le bleu du thème), Inter chargée par son index.html.
 */
export const HELIOS: Marque = {
  id: "helios",
  nom: "Helios",
  logo: null,
  icone: "/favicon.svg",
  iconeConnexion: "/favicon.svg",
  accent: null,
  themeParDefaut: "light",
  couleurDeBarre: "#ffffff",
  police: null,
  nomDansLesTextes: null,
};

/** Le domaine du serveur de Kayla, où l'API Helios sert l'écran. */
const DOMAINES_HELIOS = new Set(["5-175-233-77.sslip.io"]);

/** Les domaines de l'espace de travail Cyrano. */
const DOMAINES_CYRANO = new Set(["cyrano.work", "www.cyrano.work"]);

/** Le domaine de la marque RishFans — lui et tous ses sous-domaines. */
const DOMAINE_RISHFANS = "rishfans.com";

/**
 * La marque d'un nom d'hôte. Frontière de point : `app.rishfans.com` et
 * `rishfans.com` sont RishFans, `monrishfans.com` ne l'est pas.
 */
export function marqueDe(hostname: string | null | undefined): Marque {
  const h = (hostname ?? "").trim().toLowerCase().replace(/\.$/, "");
  if (h === DOMAINE_RISHFANS || h.endsWith(`.${DOMAINE_RISHFANS}`)) return RISHFANS;
  if (DOMAINES_CYRANO.has(h)) return CYRANO;
  if (DOMAINES_HELIOS.has(h)) return HELIOS;
  return DOZKA;
}

/** La marque de CE document — lue une fois, au chargement du module. */
export const MARQUE: Marque = marqueDe(
  typeof location !== "undefined" ? location.hostname : "",
);

/**
 * UN seul lien `rel` reste, celui de la marque. Le `<head>` de Dozka porte
 * DEUX favicons (`favicon.ico` et `pwa-192x192.png`) : n'en réécrire que le
 * premier laissait l'icône Dozka 192 px, que Chrome préfère (la plus grande) —
 * Kayla voyait le logo Dozka dans l'onglet d'app.rishfans.com (13-10-2026),
 * chaque fois que le service worker servait la coquille Dozka de son cache.
 */
function poserLeLien(doc: Document, rel: string, href: string): void {
  const [lien, ...surplus] = doc.head.querySelectorAll<HTMLLinkElement>(`link[rel="${rel}"]`);
  surplus.forEach((l) => l.remove());
  const garde = lien ?? doc.head.appendChild(doc.createElement("link"));
  garde.rel = rel;
  garde.removeAttribute("type"); // l'ancien favicon est un .svg : le type mentirait
  garde.removeAttribute("sizes"); // et sa taille, celle de l'icône Dozka
  garde.href = href;
}

function poserLaMeta(doc: Document, nom: string, contenu: string): void {
  let meta = doc.head.querySelector<HTMLMetaElement>(`meta[name="${nom}"]`);
  if (!meta) {
    meta = doc.createElement("meta");
    meta.name = nom;
    doc.head.appendChild(meta);
  }
  meta.content = contenu;
}

/**
 * Habille le DOCUMENT aux couleurs de la marque : titre d'onglet, icônes,
 * couleur de barre, police. À appeler UNE fois, avant le premier rendu
 * (`main.tsx`). Sans effet pour Dozka — son `index.html` est déjà le bon.
 *
 * L'accent et le thème par défaut, eux, ne se posent pas ici : ils passent par
 * les atomes (`themeAtom`) et le `ThemeProvider`, qui sont les seuls à écrire
 * sur `<html>` et à respecter le choix d'un utilisateur.
 */
export function appliquerLaMarque(doc: Document, marque: Marque = MARQUE): void {
  doc.documentElement.dataset.marque = marque.id;
  if (marque.id === DOZKA.id) return;

  doc.title = marque.nom;
  poserLeLien(doc, "icon", marque.icone);
  poserLeLien(doc, "apple-touch-icon", marque.icone);
  poserLaMeta(doc, "theme-color", marque.couleurDeBarre);
  poserLaMeta(doc, "apple-mobile-web-app-title", marque.nom);

  if (marque.police) {
    const { famille, feuille } = marque.police;
    if (!doc.head.querySelector(`link[href="${feuille}"]`)) {
      const css = doc.createElement("link");
      css.rel = "stylesheet";
      css.href = feuille;
      doc.head.appendChild(css);
    }
    // `--font-outfit` est LA variable de police du cockpit (cf
    // `lib/tailadmin/theme.css`) : la redéfinir change toute l'interface.
    doc.documentElement.style.setProperty(
      "--font-outfit",
      `"${famille}", system-ui, sans-serif`,
    );
  }
}

/** « Dozka » → le nom de la marque dans un texte (« Dozka Cockpit » devient
 *  le nom seul). Sans effet pour une marque sans `nomDansLesTextes`. */
export function rebrander(texte: string, marque: Marque = MARQUE): string {
  const nom = marque.nomDansLesTextes;
  if (!nom) return texte;
  return texte.replace(/Dozka Cockpit/g, nom).replace(/Dozka/g, nom);
}

/** La même réécriture sur toutes les chaînes d'un fichier de traductions —
 *  appliquée au chargement de chaque langue (`core/i18n/config.ts`). */
export function rebranderTraductions<T>(valeur: T, marque: Marque = MARQUE): T {
  if (!marque.nomDansLesTextes) return valeur;
  if (typeof valeur === "string") return rebrander(valeur, marque) as T;
  if (Array.isArray(valeur)) return valeur.map((v) => rebranderTraductions(v, marque)) as T;
  if (valeur && typeof valeur === "object") {
    return Object.fromEntries(
      Object.entries(valeur as Record<string, unknown>).map(([k, v]) => [k, rebranderTraductions(v, marque)]),
    ) as T;
  }
  return valeur;
}
